import { FC, Fragment, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useEffectOnceWhen } from "rooks";
import { IconButton, Link, Stack, Tooltip } from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { useOffHoursPreviewDatesInfoHook } from "../../utils/hooks/useOffHoursPreviewDatesInfo.hook";
import { WithoutAccountsOffHoursDialog } from "../../../common/components/grid/WithoutAccountsOffHoursDialog";
import { ScheduleResourcesPreviewDrawer } from "../../../common/components/resources-preview-old/ScheduleResourcesPreviewDrawer";
import { ScheduleResourcesPreviewDrawerAppBar } from "../../../common/components/resources-preview-old/components/app-bar/ScheduleResourcesPreviewDrawerAppBar";
import { TypographyWithTooltip } from "@/components/common/TypographyWithTooltip";
import { useDialog } from "@/utils/hooks/useDialog.hook";
import { OffHoursSchedule } from "@/services/cloudchipr.api";
import { useHover } from "@/utils/hooks/useHover.hook";
import { CopyIconButton } from "@/components/common/CopyIconButton";
import { executionLogSourceAndExecutedByFilterKey } from "@/components/pages/execution-log/utils/constants/common";
import { connectSourceAndExecutedByaFilterValue } from "@/components/pages/execution-log/utils/helpers/getSourceAndExecutedByFromSelectValues";

interface OffHoursListGridNameCellProps {
  schedule: OffHoursSchedule;
}

export const OffHoursDataGridNameCell: FC<OffHoursListGridNameCellProps> = ({
  schedule,
}) => {
  const [searchParams] = useSearchParams();
  const scheduleIdForPreview = searchParams.get("preview");

  const { open, openDialog, closeDialog } = useDialog();
  const { ref, hovered } = useHover();

  const withoutAccounts = useMemo(() => {
    return schedule.accounts.every((account) => account.status !== "connected");
  }, [schedule]);

  const editingUrl = `/automations/off-hours/edit/${schedule.id}`;
  const accountIds = schedule.accounts.map((account) => account.id);

  const { nextRuns, frequency } = useOffHoursPreviewDatesInfoHook({
    timeZone: schedule.time_zone,
    startCron: schedule.start_cron,
    stopCron: schedule.stop_cron,
    nextStartRun: schedule?.metadata?.next_start_run_at,
    nextStopRun: schedule?.metadata?.next_stop_run_at,
  });

  useEffectOnceWhen(
    openDialog,
    schedule.id === scheduleIdForPreview && !withoutAccounts,
  );

  return (
    <Stack direction="row" ref={ref} alignItems="center" spacing={1}>
      <Stack maxHeight="100%" overflow="hidden">
        {withoutAccounts ? (
          <Fragment>
            <TypographyWithTooltip
              title={schedule.name}
              onClick={openDialog}
              sx={{ textDecoration: "underline", cursor: "pointer" }}
            />

            {open && (
              <WithoutAccountsOffHoursDialog
                onClose={closeDialog}
                offHours={schedule}
              />
            )}
          </Fragment>
        ) : (
          <TypographyWithTooltip
            title={schedule.name}
            color="primary"
            onClick={openDialog}
            sx={{
              textDecoration: "underline",
              textUnderlineOffset: 4,
              cursor: "pointer",
            }}
          />
        )}

        <TypographyWithTooltip variant="body2" title={schedule.description} />
      </Stack>

      {hovered && (
        <Tooltip arrow title="See Execution Logs" placement="top">
          <IconButton
            size="small"
            component={Link}
            href={`/execution-log?${executionLogSourceAndExecutedByFilterKey}=${connectSourceAndExecutedByaFilterValue(
              "off_hours",
              schedule.name,
            )}`}
            target="_blank"
          >
            <DescriptionOutlinedIcon fontSize="small" color="action" />
          </IconButton>
        </Tooltip>
      )}

      <CopyIconButton
        visible={hovered}
        data={schedule.name}
        TooltipProps={{ title: "Copy name", placement: "top" }}
      />

      <CopyIconButton
        visible={hovered}
        data={`${window.location.href}?search=${schedule.id}`}
        TooltipProps={{ title: "Copy URL", placement: "top" }}
      >
        <LinkIcon fontSize="small" />
      </CopyIconButton>

      {schedule.filter && !withoutAccounts && (
        <ScheduleResourcesPreviewDrawer
          open={open}
          gracePeriodEnabled={false}
          scheduleId={schedule.id}
          scheduleAction={schedule.action}
          onClose={closeDialog}
          regions={schedule.regions}
          filters={schedule.filter}
          accountIds={accountIds}
          status={schedule.status}
          name={schedule.name}
          editingUrl={editingUrl}
        >
          <ScheduleResourcesPreviewDrawerAppBar
            regions={schedule.regions}
            scheduleId={schedule.id}
            nextRuns={nextRuns}
            frequency={frequency}
            accountIds={accountIds}
            filters={schedule.filter}
          />
        </ScheduleResourcesPreviewDrawer>
      )}
    </Stack>
  );
};
